import { useContext } from 'react';
import qs from 'qs';
import { get, isEmpty, uniq } from 'lodash';
import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { TeamDashContext } from 'src/pagesDashboard/DashTeam/context/Provider';
import { UserProfileContext } from 'src/pagesDashboard/UserProfile/context/UserProfileProvider';
import api from 'src/services/api';
import blobUtils from 'src/utils/blob';
import { useAccountScore } from 'src/queries/account';
import { useCompany } from 'src/queries/company';
import { useCompanyFeedback, useFeedback } from 'src/queries/feedback';
import { useReportScore } from 'src/queries/reports';
import { useReviews, useReviewsV2 } from 'src/queries/reviews';
import { useTree } from 'src/queries/tree';
import appUtils from 'src/components/appUtils';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonQuestions from 'common/commonQuestions';
import commonViewPermissions from 'common/commonViewPermissions';
import UserProfileInitialState from './context/state';

const { REVIEW_STATUS } = COMMON_CONSTANTS;

export const useReviewerAndRevieweeHistory = (userId, range = {}) => {
  const isEnabled = Boolean(userId);
  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany,
    isFetched: isFetchedCompany
  } = useCompany();

  const { start, end } = range;
  const {
    data: reviewerReviews,
    isFetching: isFetchingReviewerReviews,
    isError: isErrorReviewerReviews,
    isFetched: isFetchedReviewerReviews
  } = useReviewsV2(
    {
      reviewees: [userId],
      status: [REVIEW_STATUS.REVIEWED, REVIEW_STATUS.NOT_AVAIL],
      ...(start && {
        scheduledDate: {
          start,
          end
        }
      })
    },
    {
      page: {
        size: Number.MAX_SAFE_INTEGER
      },
      projection: ['reviewer']
    },
    {
      enabled: Boolean(isEnabled)
    }
  );
  const {
    data: revieweeReviews,
    isLoading: isFetchingRevieweeReviews,
    isError: isErrorRevieweeReviews,
    isFetched: isFetchedRevieweeReviews
  } = useReviewsV2(
    {
      reviewers: [userId],
      status: [REVIEW_STATUS.REVIEWED, REVIEW_STATUS.NOT_AVAIL],
      ...(start && {
        scheduledDate: {
          start,
          end
        }
      })
    },
    {
      page: {
        size: Number.MAX_SAFE_INTEGER
      },
      projection: ['reviewee', 'roleId', 'categoryId']
    },
    {
      enabled: Boolean(isEnabled)
    }
  );

  if (!isEnabled) {
    return {
      isEnabled,
      reviewers: [],
      reviewees: [],
      revieweeRoles: [],
      revieweeCategories: [],
      isFetching: true,
      isError: false,
      isFetched: false
    };
  }

  const reviewers = [];
  const reviewees = [];
  const revieweeRoleIds = [];
  const revieweeCategoryIds = [];
  if (!isFetchingReviewerReviews && !isFetchingRevieweeReviews) {
    reviewerReviews.forEach((review) => {
      const { reviewer } = review;
      if (!reviewers.includes(reviewer)) reviewers.push(reviewer);
    });
    revieweeReviews.forEach((review) => {
      const { reviewee, roleId, categoryId } = review;
      if (!reviewees.includes(reviewee)) reviewees.push(reviewee);
      if (roleId && !revieweeRoleIds.includes(roleId)) revieweeRoleIds.push(roleId);
      if (categoryId && !revieweeCategoryIds.includes(categoryId)) revieweeCategoryIds.push(categoryId);
    });
  }

  const revieweeRoles = [];
  const revieweeCategories = [];
  if (!isFetchingCompany && company && company.id) {
    const companyQuestions = company.questions;
    revieweeRoleIds.forEach((roleId) => {
      const role = commonQuestions.getRoleById(roleId, companyQuestions);
      if (role) {
        const { label, categories } = role;
        revieweeRoles.push({
          id: roleId,
          label,
          categories
        });
      }
    });

    revieweeCategoryIds.forEach((categoryId) => {
      const category = commonQuestions.getCategory(
        categoryId,
        companyQuestions.CATEGORIES
      );
      if (category) {
        const { label } = category;
        revieweeCategories.push({
          id: categoryId,
          label
        });
      }
    });
  }

  return {
    isEnabled,
    reviewers,
    reviewees,
    revieweeRoles,
    revieweeCategories,
    isFetching:
      isFetchingCompany
      || isFetchingReviewerReviews
      || isFetchingRevieweeReviews,
    isError: isErrorCompany || isErrorRevieweeReviews || isErrorReviewerReviews,
    isFetched:
      isFetchedCompany || isFetchedReviewerReviews || isFetchedRevieweeReviews
  };
};

const getFilterByRoles = (reviewedRoles, company, rolesFilter) => {
  if (
    !company
    || !company.questions
    || !reviewedRoles
    || !reviewedRoles.length
  ) {
    return [];
  }
  return (
    reviewedRoles
    && reviewedRoles
      .filter((roleId) => commonQuestions.getRoleById(roleId, company.questions))
      .map((roleId) => {
        const roleObj = commonQuestions.getRoleById(roleId, company.questions);
        return {
          id: roleId,
          label: roleObj.label,
          name: roleObj.label,
          checked: rolesFilter && Boolean(rolesFilter.includes(roleId))
        };
      })
      .sort((a, b) => (a.label < b.label ? -1 : 1))
  );
};

export const useUserProfile = (userId) => {
  const isEnabled = Boolean(userId);
  const contextObject = useContext(UserProfileContext);
  const defaultRange = appUtils.getDefaultRange();
  const newUserProfileInitialState = {
    ...UserProfileInitialState,
    range: defaultRange
  };
  const context = get(contextObject, 'context', newUserProfileInitialState);
  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany,
    isFetched: isFetchedCompany
  } = useCompany();
  const companyQuestions = get(company, 'questions', {});

  const [matches] = useSearchParams();

  const reportId = matches.get('reportId');

  const revieweeId = userId;
  const {
    reviewerIds,
    reviewerGroup,
    roles: rolesFilter,
    category
  } = context.filters;
  const range = context.range.value;
  const { start, end } = context.range;

  const {
    data: { feedbacks: feedbackData } = {
      feedbacks: [],
      notes: []
    },
    isFetching: isFetchingFeedback,
    isFetched: isFetchedFeedback,
    isError: isErrorFeedback,
    refetch: refetchFeedback
  } = useFeedback(
    {
      ids: [userId],
      range,
      start,
      end
    },
    {
      enabled: Boolean(isEnabled)
    }
  );

  const {
    data: score,
    isFetching: isFetchingScore,
    isError: isErrorScore,
    isFetched: isFetchedScore,
    refetch: refetchScore
  } = useAccountScore(
    {
      id: revieweeId,
      ...(start && {
        start,
        end
      }),
      reviewerIds,
      reviewerGroup,
      categoryIds: [category],
      roleIds: rolesFilter
    },
    {
      bundleCategories: true
    }
  );

  const {
    data: reportScore,
    isFetching: isFetchingReportScore,
    isError: isErrorReportScore,
    isFetched: isFetchedReportScore
  } = useReportScore(reportId);

  const {
    reviewers,
    reviewees,
    revieweeRoles,
    revieweeCategories: allRevieweeCategories,
    isFetching: isFetchingReviewerAndRevieweeHistory,
    isError: isErrorReviewerAndRevieweeHistory
  } = useReviewerAndRevieweeHistory(userId, {
    start,
    end
  });

  let feedbackReceived = '-';
  if (feedbackData) {
    feedbackReceived = feedbackData;
  }

  if (!isEnabled) {
    return {
      isEnabled,
      isFetching: true,
      isFetched: false,
      isLoading: true,
      isError: true,
      rolesData: [],
      myCategories: [],
      myRoles: [],
      revieweeRolesOptions: [],
      revieweeCategories: [],
      userId: revieweeId,
      reviewers,
      reviewees,
      filterByRoles: [],
      chart: {
        default: {
          chart: {}
        },
        options: []
      },
      feedbackReceived,
      score,
      bundledCategories: [],
      refetchUserProfile: () => {}
    };
  }

  const refetchUserProfile = async () => {
    const [feedbackResp, scoreResp] = await Promise.all([
      refetchFeedback(),
      refetchScore()
    ]);
    return { feedback: feedbackResp, score: scoreResp };
  };

  const scoreToGetCategoriesFrom = reportId ? reportScore : score;
  const bundledCategories = get(
    scoreToGetCategoriesFrom,
    'bundledCategories',
    []
  ).map((bundledCategory) => {
    const categoryData = commonQuestions.getCategory(
      bundledCategory.id,
      companyQuestions.CATEGORIES
    );

    if (!isEmpty(categoryData)) {
      return {
        ...bundledCategory,
        label: categoryData.label
      };
    }

    return {
      ...bundledCategory,
      label: 'Unknown'
    };
  });

  const reviewedRoleIds = uniq(
    bundledCategories.map((cat) => cat.roleIds).flat()
  );

  const filterByRoles = getFilterByRoles(reviewedRoleIds, company, rolesFilter);

  const { roles: filteredRoles } = context.filters;
  const revieweeRolesOptions = revieweeRoles.map((role) => ({
    id: role.id,
    label: role.label,
    checked: filteredRoles?.includes(role.id)
  }));
  let revieweeCategories = [];
  if (filteredRoles?.length) {
    const validCategoryIds = revieweeRoles
      .filter((role) => filteredRoles.includes(role.id))
      .map((role) => role.categories)
      .flat();
    revieweeCategories = allRevieweeCategories.filter((cat) => validCategoryIds.includes(cat.id));
  } else revieweeCategories = allRevieweeCategories;

  return {
    isEnabled,
    isFetching:
      isFetchingScore
      || isFetchingCompany
      || isFetchingReviewerAndRevieweeHistory
      || isFetchingScore
      || isFetchingReportScore
      || isFetchingFeedback,
    isFetched:
      isFetchedScore
      && isFetchedCompany
      && isFetchedScore
      && isFetchedReportScore,
    isLoading:
      isFetchingScore
      || isFetchingCompany
      || isFetchingReviewerAndRevieweeHistory
      || isFetchingScore
      || isFetchingReportScore
      || isFetchingFeedback,
    isError:
      isErrorScore
      || isErrorCompany
      || isErrorReviewerAndRevieweeHistory
      || isErrorScore
      || isErrorReportScore
      || isErrorFeedback,
    revieweeRolesOptions,
    revieweeCategories,
    userId: revieweeId,
    reviewers,
    reviewees,
    filterByRoles,
    feedbackReceived,
    refetchUserProfile,
    score,
    bundledCategories
  };
};

const useUserQueryParams = ({
  includeEmptyComments,
  includeAnonymous,
  includeNA
} = {}) => {
  const { context } = useContext(UserProfileContext);
  const revieweeId = context.userTree?.id;
  const {
    reviewerIds, reviewerGroup, category, roles
  } = context.filters;
  const range = context.range.value;
  const { start } = context.range;
  const { end } = context.range;

  const filters = {
    reviewerIds,
    reviewerGroup,
    category,
    roles,
    range,
    start,
    end,
    includeEmptyComments,
    includeNA,
    includeAnonymous
  };

  const { page, pageSize: size } = context.historicReviews;
  const options = { page, size };

  return { revieweeId, filters, options };
};

const useCompanyQueryParams = (currentPage) => {
  const { context } = useContext(TeamDashContext);

  const filters = {
    ...context.range,
    status: [REVIEW_STATUS.REVIEWED, REVIEW_STATUS.NOT_AVAIL]
  };
  const options = {
    size: UserProfileInitialState.historicReviews.pageSize,
    page: currentPage
  };

  return { filters, options };
};

// query to get a user's reviews
export const useUserReviews = ({
  userId,
  includeEmptyComments = true,
  includeNA = false,
  includeAnonymous = false
}) => {
  const { filters, options } = useUserQueryParams({
    includeAnonymous,
    includeEmptyComments,
    includeNA
  });
  filters.includeNA = includeNA;
  const { data, isFetching } = useReviews({
    userId,
    filters,
    options
  });

  return {
    reviews: data.reviews,
    pagination: data.pagination,
    isLoading: isFetching
  };
};

// query to get a company's reviews
export const useCompanyReviews = (
  companyId,
  currentPage,
  queryOptions = {}
) => {
  const { filters, options } = useCompanyQueryParams(currentPage);

  const { data, isFetching, isError } = useReviews({
    companyId,
    filters,
    options,
    queryOptions
  });

  return {
    reviews: data.reviews,
    pagination: data.pagination,
    isFetching,
    isError
  };
};

export const useUserReportReviews = (
  userId,
  categoryId,
  roleId,
  reportRoleView
) => {
  const { filters, options } = useUserQueryParams({
    includeAnonymous: true
  });
  const {
    data: { tree, myTreeRow },
    isFetching: isFetchingTree
  } = useTree();
  filters.category = categoryId;
  if (reportRoleView) {
    filters.roles = [roleId];
  }
  const viewerId = myTreeRow.id;
  const enabled = commonViewPermissions.canViewUserReportHistoricReviews(
    tree,
    viewerId,
    userId
  );
  filters.includeEmptyComments = true;
  const queryOptions = { enabled };
  const { data, isFetching } = useReviews({
    userId,
    filters,
    options,
    queryOptions
  });
  const topReviewsOptions = {
    ...options,
    page: '1',
    sortBy: 'score',
    sortOrder: 'desc'
  };
  const { data: dataTopReviews } = useReviews({
    userId,
    filters,
    options: topReviewsOptions,
    queryOptions
  });
  const bottomReviewsOptions = {
    ...options,
    page: '1',
    sortBy: 'score',
    sortOrder: 'asc'
  };
  const { data: dataBottomReviews } = useReviews({
    userId,
    filters,
    options: bottomReviewsOptions,
    queryOptions
  });
  let reviews = data.reviews || [];
  reviews = reviews.filter((r) => r.categoryId === categoryId);

  return {
    reviews: data.reviews || [],
    pagination: data.pagination,
    topReviews: dataTopReviews.reviews || [],
    bottomReviews: dataBottomReviews.reviews || [],
    isLoading: isFetching
  };
};

export const useHistoricReviews = ({ userId, filters, options }) => {
  const { data, isFetching } = useReviews({ userId, filters, options });

  return {
    reviews: data.reviews,
    isLoading: isFetching
  };
};

// query to get a user's reviews in a spreadsheet
export const useDownloadUserReviewsQuery = () => {
  const { revieweeId, filters, options } = useUserQueryParams({
    includeAnonymous: true,
    includeEmptyComments: true
  });

  return useMutation(async (filename) => {
    const params = qs.stringify(
      { userId: revieweeId, filters, options },
      { skipNulls: true }
    );
    const blob = await api.getXLS(`/user/export?${params}`);
    blobUtils.triggerBlobDownload(blob, filename);
  });
};
